import { LitElement, PropertyValues, html, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SDSPopoverTargetMixin } from '../mixins/sds-popover-target-mixin';
import { SDS_SIZES, SDS_VARIANTS } from '../types';
import { styles } from './sds-button.styles';
import { SDSEventDispatcherMixin, SDSDelegateInteractionMixin } from '../mixins';

type ButtonType = 'button' | 'submit' | 'reset';
type ButtonVariant = SDS_VARIANTS | 'critical';
type ButtonSize = 'large' | 'medium' | 'small' | Extract<SDS_SIZES, 'lg' | 'md' | 'sm'>;
type TargetTypes = '_self' | '_blank' | '_parent' | '_top';

export interface SDSButtonProps {
	disabled?: boolean;
	form?: string;
	href?: string;
	icon?: boolean;
	loading?: boolean;
	name?: string;
	popoverTarget?: string;
	popoverTargetAction?: string;
	size?: ButtonSize;
	target?: TargetTypes;
	type?: ButtonType;
	value?: string;
	variant?: ButtonVariant;
}

export class SDSButtonComponent extends SDSPopoverTargetMixin(SDSDelegateInteractionMixin(SDSEventDispatcherMixin(LitElement))) {
	static override styles = [styles];

	static formAssociated = true;
	public readonly internals: ElementInternals;

	static override shadowRootOptions: ShadowRootInit = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	};

	@property({ type: Boolean, reflect: true }) disabled: boolean = false;
	@property({ type: Boolean, reflect: true }) icon: boolean = false;
	@property({ type: Boolean, reflect: true }) loading: boolean = false;
	@property({ type: String, reflect: true }) form?: string;
	@property({ type: String, reflect: true }) href?: string;
	@property({ type: String, reflect: true }) name?: string;
	@property({ type: String, reflect: true }) size: ButtonSize = 'lg';
	@property({ type: String, reflect: true }) target?: TargetTypes;
	@property({ type: String, reflect: true }) type: ButtonType = 'button';
	@property({ type: String, reflect: true }) value: string = '';
	@property({ type: String, reflect: true }) variant: ButtonVariant = 'primary';

	@query('#control') control!: HTMLButtonElement;

	constructor() {
		super();
		this.internals = this.attachInternals();
	}

	override updated(changed: PropertyValues) {
		super.updated(changed);

		// Cast critical variant to danger palette to prevent breaking changes
		if (changed.has('variant') && this.variant === 'critical') {
			this.setAttribute('data-palette', 'danger');
		}
	}

	private readonly _handleClick = (event: MouseEvent) => {
		// For popover triggers, let the browser handle the toggle natively.
		// Stop propagation and re-dispatch a composed click for listeners on the host.
		if (this.popoverTarget) {
			event.stopImmediatePropagation();
			if (!this.disabled) {
				this.dispatchSheriffEvent('click');
			}
			return;
		}

		event.preventDefault();
		event.stopImmediatePropagation();

		if (!this.disabled) {
			switch (this.type) {
				case 'submit': {
					const shouldSubmit = this.dispatchSheriffEvent('submit');

					if (shouldSubmit) {
						if (this.form) {
							const root = this.getRootNode() as Document | ShadowRoot;
							const formElement: HTMLFormElement | null = root.querySelector(`form#${this.form}`);
							formElement?.requestSubmit();
						} else if (this.internals.form) {
							this.internals.form.requestSubmit();
						}
					}
					break;
				}

				case 'reset': {
					const shouldReset = this.dispatchSheriffEvent('reset');

					if (shouldReset) {
						if (this.form) {
							const root = this.getRootNode() as Document | ShadowRoot;
							const formElement: HTMLFormElement | null = root.querySelector(`form#${this.form}`);
							formElement?.reset();
						} else if (this.internals.form) {
							this.internals.form.reset();
						}
					}
					break;
				}

				default:
					this.dispatchSheriffEvent('click');
			}
		}
	};

	override render() {
		const buttonClasses = {
			'sds-button': true,
			'sds-button--as-link': !!this.href,
		};

		if (this.href) {
			return html` <a id="link" class="${classMap(buttonClasses)}" href="${this.href}" target="${ifDefined(this.target)}">
				<slot name="before"></slot>
				<slot></slot>
				<slot name="after"></slot>
			</a>`;
		}

		return html`
			<button
				id="control"
				form="${ifDefined(this.form)}"
				class="${classMap(buttonClasses)}"
				type="${this.type}"
				name="${ifDefined(this.name)}"
				value="${ifDefined(this.value)}"
				?disabled="${this.disabled}"
				@click="${this._handleClick}"
			>
				${this.loading ? html`<sds-loader slot="before" ?contrast="${this.variant !== 'primary'}"></sds-loader>` : nothing} ${this.loading ? nothing : html`<slot name="before"></slot>`}
				<slot></slot>
				${this.loading ? nothing : html`<slot name="after"></slot>`}
			</button>
		`;
	}
}
